import * as React from 'react';
import { escape, unescape } from 'lodash';
import { NavigateOptions, useSearchParams } from 'react-router-dom';
import { CreateCompanyPayload } from 'services/clients/user';

export function jsonToB64(data: object): string {
  return window.btoa(escape(encodeURIComponent(JSON.stringify(data))));
}

export function b64ToJson(data: string): object {
  return JSON.parse(decodeURIComponent(unescape(window.atob(data))));
}

export function determineFlow(companyType: CreateCompanyPayload['company_type_detail']) {
  switch (companyType) {
    case 'BCO / Consignee':
    case '3PL':
    case 'Freight Forwarder':
    case 'Other':
      return 'shipper';
    default:
      return 'trucker';
  }
}

export interface CreateFormValues {
  type: CreateCompanyPayload['company_type_detail'];
  name: string;
  scac?: string;
  autoJoinEmailDomain: boolean;
  otherType?: string;
}

export interface CreateCompanyStage extends Partial<CreateFormValues> {
  stage: 'create-company';
}

export interface DetailCompanyStage extends CreateFormValues {
  stage: 'detail-company';
}

export interface InviteStage {
  stage: 'invite';
  type: CreateCompanyPayload['company_type_detail'];
}

export type State = CreateCompanyStage | DetailCompanyStage | InviteStage;

export function useCreateCompanyState(): [
  State,
  (newState: State, navigateOptions?: NavigateOptions) => void
] {
  const [searchParams, setSearchParams] = useSearchParams();

  const state: State = React.useMemo(() => {
    const stateString = searchParams.get('state');
    return stateString ? (b64ToJson(stateString) as State) : { stage: 'create-company' };
  }, [searchParams]);

  const setState = React.useCallback(
    (newState: State, navigateOptions?: NavigateOptions) => {
      setSearchParams({ state: jsonToB64(newState) }, navigateOptions);
    },
    [setSearchParams]
  );

  return [state, setState];
}
